"use server";

import { redirect } from "next/navigation";
import { prisma } from "@/lib/prisma";
import { getCurrentUser, getPowerState, splitCharge } from "@/lib/session";
import { buyerHandle } from "@/lib/anon";
import { remaining, isSoldOut } from "@/lib/drops";
import { sendEmail } from "@/lib/notify";
import { sendOrderInvoiceEmail } from "@/lib/order-payment";
import { money } from "@/lib/money";

// Where order alerts land. Every Take click is a lead and the wire flow starts
// with a human reaching out, so the moment an order exists it goes to the
// inbox that acts on it.
const ORDER_ALERT_EMAIL = "lennon@clearlinewholesale.com";

// Feed "Take All": register interest and email the buyer an auto-generated
// invoice (with wire details), then return to the feed with a confirmation.
// No onboarding / spending-power gate: any logged-in user can place it.
export async function takeAllAction(formData: FormData): Promise<void> {
  const user = await getCurrentUser();
  const listingId = String(formData.get("listingId") ?? "");
  const qtyRaw = Math.max(1, Number(formData.get("quantity") ?? 0));

  if (!user) redirect(`/login?next=${encodeURIComponent("/feed")}`);

  const listing = await prisma.listing.findUnique({ where: { id: listingId } });
  if (!listing || listing.status !== "LIVE" || isSoldOut(listing))
    redirect("/feed?err=unavailable");
  const qty = Math.min(qtyRaw, remaining(listing!));
  if (qty < 1) redirect("/feed?err=unavailable");

  // The feed presents Clearline cost pricing, so the invoice bills at cost.
  const unitPriceCents = listing!.costCents;

  // The order must fit inside the buyer's remaining spending power, and it
  // draws that power down: the monthly membership allowance first, then the
  // free onboarding grant. The feed's profit calculator flags "Over Limit";
  // the order itself enforces it too, so Take Min / custom amount / Take All
  // can't push a buyer past their power. getPowerState settles any lapsed
  // billing period before reading the wallets.
  const costCents = unitPriceCents * qty;
  const power = await getPowerState(user);
  if (costCents > power.remainingCents) redirect("/feed?err=power");
  const { fromMonthlyCents, fromBonusCents } = splitCharge(
    costCents,
    power.monthlyRemainingCents,
  );

  const [order] = await prisma.$transaction([
    prisma.order.create({
      data: {
        listingId: listing!.id,
        buyerId: user!.id,
        buyerHandle: buyerHandle(user!.id),
        quantity: qty,
        unitPriceCents,
        totalCents: costCents,
        fromMonthlyCents,
        fromBonusCents,
        paymentMethod: "WIRE",
        status: "PENDING",
      },
    }),
    prisma.listing.update({
      where: { id: listing!.id },
      data: {
        quantitySold: { increment: qty },
        status:
          listing!.quantitySold + qty >= listing!.quantity ? "SOLD_OUT" : "LIVE",
      },
    }),
    // Draw the power down. Only touch a wallet it actually funded.
    ...(fromMonthlyCents > 0
      ? [
          prisma.membership.update({
            where: { userId: user!.id },
            data: {
              spendingPowerRemainingCents: { decrement: fromMonthlyCents },
            },
          }),
        ]
      : []),
    ...(fromBonusCents > 0
      ? [
          prisma.user.update({
            where: { id: user!.id },
            data: { bonusSpendingPowerCents: { decrement: fromBonusCents } },
          }),
        ]
      : []),
  ]);

  // Auto-generate + email the invoice (Stripe bank-transfer invoice with wire
  // details) to the buyer. Awaited so it sends before the function returns;
  // guarded so a mail/Stripe hiccup never blocks the confirmation.
  try {
    await sendOrderInvoiceEmail(order.id, {
      subject: `Your Clearline invoice: ${listing!.title}`,
      message: `You reserved ${qty.toLocaleString()} units of ${listing!.title} for ${money(unitPriceCents * qty)}. Settle by bank transfer using the wire details on the invoice below.`,
    });
  } catch {}

  // Desk heads-up.
  void sendEmail(
    ORDER_ALERT_EMAIL,
    `Order: ${qty.toLocaleString()}x ${listing!.title} · ${money(unitPriceCents * qty)}`,
    [
      `<p><strong>${qty.toLocaleString()} units</strong> of <strong>${listing!.title}</strong> at ${money(unitPriceCents)}/unit — <strong>${money(unitPriceCents * qty)}</strong> total.</p>`,
      `<p>Buyer: ${user!.name ?? "-"} (${buyerHandle(user!.id)})<br/>Email: ${user!.email}<br/>Phone: ${user!.phone ?? "-"}</p>`,
    ].join("\n"),
  ).catch(() => {});

  redirect("/feed?placed=1");
}
